import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, ShoppingCart, BookOpen, Send, CheckCircle2, AlertCircle, Copy, ExternalLink, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";

export default function Portal() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [linkModal, setLinkModal] = useState(null); // {nome, url}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketplace/me");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar portal");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === "autor") load(); }, [user]);

  if (user && user.role !== "autor") return <Navigate to="/" replace />;

  const t = data?.totals || {};
  const minRequest = data?.royalty_min_request || 100;
  const canRequest = (t.royalties_disponivel || 0) >= minRequest;

  const tryRequest = () => {
    if (!canRequest) {
      toast.warning(`Você ainda não tem o valor mínimo de ${fmtBRL(minRequest)} para solicitar (saldo atual: ${fmtBRL(t.royalties_disponivel || 0)}).`);
      return;
    }
    setShowRequest(true);
  };

  return (
    <Layout title="Portal do Autor" subtitle={`Bem-vindo(a), ${data?.client?.nome || user?.name || ""}`}>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Vendas totais</p>
          <p className="kpi-num text-2xl mt-2" data-testid="portal-kpi-vendas">{fmtBRL(t.vendas || 0)}</p>
        </Card>
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Royalties acumulados</p>
          <p className="kpi-num text-2xl mt-2">{fmtBRL(t.royalties || 0)}</p>
          <p className="text-[10px] text-muted-foreground mt-1">15% sobre as vendas</p>
        </Card>
        <Card className={`p-4 rounded-sm border-2 ${canRequest ? "border-[hsl(var(--primary)/0.5)] bg-[hsl(var(--primary)/0.06)]" : "border-border"}`}>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Disponível para sacar</p>
          <p className={`kpi-num text-2xl mt-2 ${canRequest ? "text-[hsl(var(--primary))]" : ""}`} data-testid="portal-kpi-disponivel">{fmtBRL(t.royalties_disponivel || 0)}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Mínimo p/ solicitar: {fmtBRL(minRequest)}</p>
        </Card>
        <Card className="p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Já recebidos</p>
          <p className="kpi-num text-2xl mt-2 text-emerald-700">{fmtBRL(t.royalties_pagos || 0)}</p>
        </Card>
      </div>

      <div className="flex justify-end mb-4">
        <Button data-testid="portal-request-btn" size="lg" onClick={tryRequest} disabled={loading}>
          <Send className="w-4 h-4 mr-2" />Solicitar Royalties
        </Button>
      </div>

      {!canRequest && (t.royalties_disponivel || 0) > 0 && (
        <Card className="p-3 mb-4 rounded-sm border-amber-200 bg-amber-50/50">
          <div className="flex items-start gap-2 text-xs text-amber-800">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              Você ainda não atingiu o saldo mínimo de <b>{fmtBRL(minRequest)}</b> para solicitar royalties.
              Saldo atual: <b>{fmtBRL(t.royalties_disponivel || 0)}</b>. Continue acumulando vendas e tente novamente em breve.
            </div>
          </div>
        </Card>
      )}

      {/* Books summary */}
      <Card className="rounded-sm mb-4 overflow-hidden">
        <div className="p-3 border-b border-border flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[hsl(var(--primary))]" />
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Seus livros</span>
        </div>
        <Table>
          <TableHeader className="bg-secondary/60"><TableRow>
            <TableHead>Título</TableHead><TableHead>ISBN</TableHead>
            <TableHead>Marketplaces</TableHead>
            <TableHead className="text-right">Vendas</TableHead><TableHead className="text-right">Faturamento</TableHead><TableHead className="text-right">Royalties</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data?.books || []).map(b => (
              <TableRow key={b.id} data-testid={`portal-book-${b.id}`}>
                <TableCell className="font-medium">{b.titulo}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{b.isbn || "—"}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(b.marketplace_links || []).length === 0 ? (
                      <span className="text-[10px] text-muted-foreground italic">sem links</span>
                    ) : (
                      (b.marketplace_links || []).map((l, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLinkModal({ nome: l.nome, url: l.url, titulo: b.titulo })}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.3)] rounded-sm hover:bg-[hsl(var(--primary)/0.2)] transition"
                          data-testid={`portal-link-${b.id}-${i}`}
                        >
                          <LinkIcon className="w-3 h-3" />
                          {l.nome}
                        </button>
                      ))
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">{b.vendas}</TableCell>
                <TableCell className="text-right">{fmtBRL(b.valor_vendas)}</TableCell>
                <TableCell className="text-right text-[hsl(var(--primary))] font-semibold">{fmtBRL(b.royalties_total)}</TableCell>
              </TableRow>
            ))}
            {(data?.books || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nenhum livro cadastrado ainda.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* Sales */}
      <Card className="rounded-sm mb-4 overflow-hidden">
        <div className="p-3 border-b border-border flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-[hsl(var(--primary))]" />
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Vendas nos marketplaces</span>
        </div>
        <Table>
          <TableHeader className="bg-secondary/60"><TableRow>
            <TableHead>Livro</TableHead><TableHead>Marketplace</TableHead><TableHead>Data</TableHead>
            <TableHead className="text-right">Valor</TableHead><TableHead className="text-right">Royalty</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data?.sales || []).map(s => {
              const bk = (data?.books || []).find(b => b.id === s.book_id);
              const sm = {
                livre: { c: "text-emerald-700 border-emerald-200", l: "Disponível" },
                solicitado: { c: "text-amber-700 border-amber-200", l: "Solicitado" },
                pago: { c: "text-blue-700 border-blue-200", l: "Pago" },
              };
              const m = sm[s.royalty_status] || sm.livre;
              return (
                <TableRow key={s.id} data-testid={`portal-sale-${s.id}`}>
                  <TableCell className="font-medium">{bk?.titulo || "—"}</TableCell>
                  <TableCell>{s.marketplace}</TableCell>
                  <TableCell>{fmtDate(s.data_venda)}</TableCell>
                  <TableCell className="text-right">{fmtBRL(s.valor_venda)}</TableCell>
                  <TableCell className="text-right text-[hsl(var(--primary))] font-semibold">{fmtBRL(s.royalty_value)}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${m.c}`}>{m.l}</Badge></TableCell>
                </TableRow>
              );
            })}
            {(data?.sales || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nenhuma venda lançada ainda.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* Royalty requests history */}
      <Card className="rounded-sm overflow-hidden">
        <div className="p-3 border-b border-border flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[hsl(var(--primary))]" />
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Solicitações de royalties</span>
        </div>
        <Table>
          <TableHeader className="bg-secondary/60"><TableRow>
            <TableHead>Solicitada em</TableHead><TableHead className="text-right">Valor</TableHead>
            <TableHead>Conta / PIX</TableHead><TableHead>Previsão de pagamento</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data?.requests || []).map(r => (
              <TableRow key={r.id} data-testid={`portal-req-${r.id}`}>
                <TableCell className="text-xs">{fmtDate(r.requested_at?.slice(0, 10))}</TableCell>
                <TableCell className="text-right font-semibold">{fmtBRL(r.total_valor)}</TableCell>
                <TableCell className="text-xs">
                  {r.banco}{r.agencia ? ` · Ag ${r.agencia}` : ""}{r.conta ? ` · Cc ${r.conta}` : ""}{r.pix_chave ? ` · PIX ${r.pix_chave}` : ""}
                </TableCell>
                <TableCell className="text-xs">{fmtDate(r.prev_pagamento)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] ${r.status === "pago" ? "text-emerald-700 border-emerald-200" : "text-amber-700 border-amber-200"}`}>
                    {r.status === "pago" ? <><CheckCircle2 className="w-3 h-3 inline mr-1" />Pago em {fmtDate(r.paid_at?.slice(0, 10))}</> : "Pendente"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
            {(data?.requests || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Nenhuma solicitação ainda.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <RequestDialog
        open={showRequest}
        onClose={() => setShowRequest(false)}
        total={t.royalties_disponivel || 0}
        authorName={data?.client?.nome}
        onDone={() => { setShowRequest(false); load(); toast.success("Solicitação enviada ao financeiro"); }}
      />

      <MarketplaceLinkModal link={linkModal} onClose={() => setLinkModal(null)} />
    </Layout>
  );
}

function MarketplaceLinkModal({ link, onClose }) {
  if (!link) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success("Link copiado");
      onClose();
    } catch {
      // fallback
      const el = document.createElement("textarea");
      el.value = link.url;
      document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el);
      toast.success("Link copiado");
      onClose();
    }
  };
  const openTab = () => {
    window.open(link.url, "_blank", "noopener,noreferrer");
    onClose();
  };
  return (
    <Dialog open={!!link} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LinkIcon className="w-4 h-4" />{link.nome}</DialogTitle>
          <DialogDescription>
            {link.titulo ? <>Livro: <b>{link.titulo}</b></> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="px-1 py-2">
          <div className="text-[11px] text-muted-foreground mb-1">URL:</div>
          <div className="font-mono text-xs break-all bg-muted/40 p-2 rounded-sm border border-border" data-testid="portal-link-url">{link.url}</div>
        </div>
        <DialogFooter className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={copy} data-testid="portal-link-copy">
            <Copy className="w-4 h-4 mr-2" />Copiar link
          </Button>
          <Button onClick={openTab} data-testid="portal-link-open">
            <ExternalLink className="w-4 h-4 mr-2" />Abrir em nova aba
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestDialog({ open, onClose, total, authorName, onDone }) {
  const [form, setForm] = useState({ banco: "", agencia: "", conta: "", tipo_conta: "Corrente", titular: "", cpf_titular: "", pix_chave: "" });
  useEffect(() => {
    if (!open) return;
    // Try to pre-fill from saved payment-info; falls back to authorName
    api.get("/profile/payment-info").then(r => {
      const d = r.data || {};
      setForm({
        banco: d.banco || "", agencia: d.agencia || "", conta: d.conta || "",
        tipo_conta: d.tipo_conta || "Corrente",
        titular: d.titular || authorName || "",
        cpf_titular: d.cpf_titular || "", pix_chave: d.pix_chave || "",
      });
    }).catch(() => setForm(f => ({ ...f, titular: authorName || "" })));
  }, [open, authorName]);

  const submit = async () => {
    if (!form.banco && !form.pix_chave) return toast.error("Informe banco/conta OU chave PIX");
    try {
      await api.post("/marketplace/royalties/request", form);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao solicitar"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Solicitar royalties — {fmtBRL(total)}</DialogTitle>
          <DialogDescription>
            O valor será enviado ao financeiro da Marupiara. Previsão de pagamento: até o último dia útil 2 meses após a solicitação.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Titular</Label><Input data-testid="req-titular" value={form.titular} onChange={(e) => setForm(f => ({ ...f, titular: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">CPF do titular</Label><Input data-testid="req-cpf" value={form.cpf_titular} onChange={(e) => setForm(f => ({ ...f, cpf_titular: e.target.value }))} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Banco</Label><Input data-testid="req-banco" value={form.banco} onChange={(e) => setForm(f => ({ ...f, banco: e.target.value }))} className="mt-1" placeholder="Ex.: Itaú, Nubank…" /></div>
            <div><Label className="text-xs font-semibold">Tipo de conta</Label>
              <Select value={form.tipo_conta} onValueChange={(v) => setForm(f => ({ ...f, tipo_conta: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Corrente">Corrente</SelectItem><SelectItem value="Poupança">Poupança</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Agência</Label><Input data-testid="req-agencia" value={form.agencia} onChange={(e) => setForm(f => ({ ...f, agencia: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Conta</Label><Input data-testid="req-conta" value={form.conta} onChange={(e) => setForm(f => ({ ...f, conta: e.target.value }))} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs font-semibold">Chave PIX (preferível, opcional se já preencher banco/conta)</Label><Input data-testid="req-pix" value={form.pix_chave} onChange={(e) => setForm(f => ({ ...f, pix_chave: e.target.value }))} className="mt-1" placeholder="CPF, e-mail, telefone ou aleatória" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} data-testid="req-confirm"><Send className="w-4 h-4 mr-2" />Enviar solicitação</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
